import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('uncut_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Photo Upload - requires auth
export const uploadPhoto = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/upload-photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeaders()
    }
  });
  return response.data;
};

// PDF Upload - works without auth
export const uploadPdf = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const headers = { 'Content-Type': 'multipart/form-data' };
  const token = localStorage.getItem('uncut_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axios.post(`${API}/upload-pdf`, formData, { headers });
  return response.data;
};

// Upload pitch deck to Supabase storage (public endpoint)
export const uploadPitchDeck = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/upload-pitch-deck`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Text Processing - works without auth
export const processText = async (text) => {
  const formData = new FormData();
  formData.append('text', text);
  
  const headers = { 'Content-Type': 'multipart/form-data' };
  const token = localStorage.getItem('uncut_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axios.post(`${API}/process-text`, formData, { headers });
  return response.data;
};

// Process Upload with LLM - works without auth
export const processUpload = async (uploadId) => {
  const headers = {};
  const token = localStorage.getItem('uncut_token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axios.post(`${API}/process-upload/${uploadId}`, null, { headers });
  return response.data;
};

// Generate Page
export const generatePage = async (uploadId, answers) => {
  const response = await axios.post(`${API}/generate-page/${uploadId}`, { answers }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Request Email Change
export const requestEmailChange = async (newEmail) => {
  const response = await axios.post(`${API}/auth/change-email`, { new_email: newEmail }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get Featured Profiles (public - for landing page)
export const getFeaturedProfiles = async (limit = 3) => {
  const response = await axios.get(`${API}/featured-profiles?limit=${limit}`);
  return response.data;
};

// Join Waitlist (public)
export const joinWaitlist = async (email, website = '') => {
  const response = await axios.post(`${API}/waitlist`, { email, website });
  return response.data;
};

// Confirm Email Change
export const confirmEmailChange = async (token) => {
  const response = await axios.post(`${API}/auth/confirm-email?token=${token}`);
  return response.data;
};

// Get My Page
export const getMyPage = async () => {
  const response = await axios.get(`${API}/pages/my`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get Page by ID
export const getPage = async (pageId) => {
  const response = await axios.get(`${API}/pages/${pageId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Update Page
export const updatePage = async (pageId, data) => {
  const response = await axios.put(`${API}/pages/${pageId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Publish/Unpublish Page
export const togglePublish = async (pageId) => {
  const response = await axios.post(`${API}/pages/${pageId}/publish`, null, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Add Card
export const addCard = async (pageId, card) => {
  const response = await axios.post(`${API}/pages/${pageId}/cards`, card, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Update Card
export const updateCard = async (pageId, cardId, data) => {
  const response = await axios.put(`${API}/pages/${pageId}/cards/${cardId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Delete Card
export const deleteCard = async (pageId, cardId) => {
  const response = await axios.delete(`${API}/pages/${pageId}/cards/${cardId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Reorder Cards
export const reorderCards = async (pageId, cardIds) => {
  const response = await axios.put(`${API}/pages/${pageId}/cards/reorder`, { card_ids: cardIds }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get Public Page
export const getPublicPage = async (slug) => {
  const response = await axios.get(`${API}/p/${slug}`);
  return response.data;
};

// Track Page Visit
export const trackPageVisit = async (slug) => {
  const response = await axios.post(`${API}/p/${slug}/visit`);
  return response.data;
};

// Admin: Get Users
export const adminGetUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Get Pages
export const adminGetPages = async () => {
  const response = await axios.get(`${API}/admin/pages`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Get Waitlist
export const adminGetWaitlist = async () => {
  const response = await axios.get(`${API}/admin/waitlist`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Get Settings
export const adminGetSettings = async () => {
  const response = await axios.get(`${API}/admin/settings`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Update Settings
export const adminUpdateSettings = async (settings) => {
  const response = await axios.put(`${API}/admin/settings`, settings, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Create Profile
export const adminCreateProfile = async (email, imageUrl) => {
  const response = await axios.post(`${API}/admin/create-profile`, {
    email,
    image_url: imageUrl
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Get Page (any page)
export const adminGetPage = async (pageId) => {
  const response = await axios.get(`${API}/admin/pages/${pageId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Update Page (any page)
export const adminUpdatePage = async (pageId, data) => {
  const response = await axios.put(`${API}/admin/pages/${pageId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Toggle Publish (any page)
export const adminTogglePublish = async (pageId) => {
  const response = await axios.post(`${API}/admin/pages/${pageId}/publish`, null, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Add Card (any page)
export const adminAddCard = async (pageId, card) => {
  const response = await axios.post(`${API}/admin/pages/${pageId}/cards`, card, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Update Card (any page)
export const adminUpdateCard = async (pageId, cardId, data) => {
  const response = await axios.put(`${API}/admin/pages/${pageId}/cards/${cardId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Delete Card (any page)
export const adminDeleteCard = async (pageId, cardId) => {
  const response = await axios.delete(`${API}/admin/pages/${pageId}/cards/${cardId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Reorder Cards (any page)
export const adminReorderCards = async (pageId, cardIds) => {
  const response = await axios.put(`${API}/admin/pages/${pageId}/cards/reorder`, { card_ids: cardIds }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Admin: Upload Photo (any page)
export const adminUploadPhoto = async (pageId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/upload-photo/${pageId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeaders()
    }
  });
  return response.data;
};

// Admin: Extract profile from image
export const adminExtractFromImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/extract-from-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeaders()
    }
  });
  return response.data;
};

// Admin: Enrich profile from LinkedIn PDF
export const adminEnrichFromPdf = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/enrich-from-pdf`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeaders()
    }
  });
  return response.data;
};

// Admin: Create profile from extracted data
export const adminCreateProfileFromExtraction = async (email, extracted) => {
  const response = await axios.post(`${API}/admin/create-profile-from-extraction`, 
    { email, extracted },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Admin: Delete a page
export const adminDeletePage = async (pageId) => {
  const response = await axios.delete(`${API}/admin/pages/${pageId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Unfurl link - fetch Open Graph metadata
export const unfurlLink = async (url) => {
  const response = await axios.get(`${API}/unfurl`, {
    params: { url }
  });
  return response.data;
};


// Podcast search - public endpoint for autocomplete
export const searchPodcasts = async (query) => {
  if (!query || query.length < 1) return { suggestions: [] };
  const response = await axios.get(`${API}/podcasts/search`, {
    params: { query, limit: 10 }
  });
  return response.data;
};

// Newsletter search - public endpoint for autocomplete
export const searchNewsletters = async (query) => {
  if (!query || query.length < 1) return { suggestions: [] };
  const response = await axios.get(`${API}/newsletters/search`, {
    params: { query, limit: 10 }
  });
  return response.data;
};

// Book search - public endpoint for autocomplete
export const searchBooks = async (query) => {
  if (!query || query.length < 1) return { suggestions: [] };
  const response = await axios.get(`${API}/books/search`, {
    params: { query, limit: 10 }
  });
  return response.data;
};

// ==================== APPS API ====================

// Add app to page
export const addAppToPage = async (pageId, appConfig) => {
  const response = await axios.post(`${API}/pages/${pageId}/apps`, appConfig, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Update app config
export const updateAppConfig = async (pageId, appId, appConfig) => {
  const response = await axios.put(`${API}/pages/${pageId}/apps/${appId}`, appConfig, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Delete app from page
export const deleteAppFromPage = async (pageId, appId) => {
  const response = await axios.delete(`${API}/pages/${pageId}/apps/${appId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Get public app config (for form rendering)
export const getPublicAppConfig = async (slug, appType) => {
  const response = await axios.get(`${API}/public/pages/${slug}/apps/${appType}`);
  return response.data;
};

// Submit app form (e.g., pitch submission)
export const submitAppForm = async (slug, appType, data, files = []) => {
  const response = await axios.post(`${API}/public/pages/${slug}/apps/${appType}/submit`, {
    data,
    files
  });
  return response.data;
};

// Get page submissions (admin)
export const getPageSubmissions = async (pageId) => {
  const response = await axios.get(`${API}/admin/pages/${pageId}/submissions`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Update submission status (admin)
export const updateSubmissionStatus = async (submissionId, status) => {
  const response = await axios.put(`${API}/admin/submissions/${submissionId}/status`, null, {
    params: { status },
    headers: getAuthHeaders()
  });
  return response.data;
};

// Delete submission (admin)
export const deleteSubmission = async (submissionId) => {
  const response = await axios.delete(`${API}/admin/submissions/${submissionId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Update submission notes (admin)
export const updateSubmissionNotes = async (submissionId, notes) => {
  const response = await axios.put(`${API}/admin/submissions/${submissionId}/notes`, null, {
    headers: getAuthHeaders(),
    params: { notes }
  });
  return response.data;
};
